import { AnimatedBox, NextImage } from '@apeiron/temp'
import { ApeironButton } from '@apeiron/ui'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import ConversionPNG from '@public/images/image-battle-demo-conversion.png'
import MobileConversionPNG from '@public/images/image-battle-demo-mobile-conversion.png'
import BurnSection from '@src/components/battle/demo/pass/BurnSection'
import BurnSuccessPopup from '@src/components/battle/demo/pass/BurnSuccessPopup'
import FragmentSection from '@src/components/battle/demo/pass/FragmentSection'
import { useBattlepassContext } from '@src/contexts/battle/demo/BattlepassContext'
import useBurnPlaytime from '@src/hooks/contract/useBurnPlaytime'
import theme from '@src/styles/theme'
import { BattleDemoFragment } from '@src/types/battleDemo'
import * as R from 'ramda'
import { FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(AnimatedBox)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 300px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    max-width: 1100px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    max-width: 550px;
  }
`

const ConversionFlow = styled(NextImage)`
  ${props => props.theme.breakpoints.up('res1024')} {
    aspect-ratio: 1.82;
    width: calc(100% - 50px);
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    aspect-ratio: 1.26;
    width: 100%;
  }
`

const FragmentContainer = styled(Box)`
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    margin-top: -14%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    margin-top: -16%;
  }
`

const StyledBurnSection = styled(BurnSection)`
  display: flex;
  position: relative;
  ${props => props.theme.breakpoints.up('res1024')} {
    margin-top: -12.5%;
  }
  ${props => props.theme.breakpoints.between('res480', 'res1024')} {
    margin-top: -5%;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    margin-top: -2%;
    max-width: 350px;
  }
`

const ButtonSection = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 15px;
`

const BurnButton = styled(ApeironButton)`
  ${props => props.theme.breakpoints.down('res1024')} {
    min-width: 220px;
    &.MuiButton-text {
      font-size: 16px;
    }
  }
`

const Remark = styled(Typography)`
  background-color: rgba(0, 0, 0, 0.5);
  font-weight: 300;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    border-radius: 1000px;
    font-size: 16px;
    line-height: 18px;
    padding: 10px 25px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    border-radius: 16px;
    font-size: 14px;
    line-height: 16px;
    max-width: 250px;
    padding: 12px 25px;
  }
`

const BattlepassContainer: FC<Props> = (props: Props) => {
  const { className, show } = props

  const { burnCount, fragments, reloadData, setBurnSuccess } =
    useBattlepassContext()

  const { burnPlaytime } = useBurnPlaytime()

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const handleOnBurnTime = useCallback(async () => {
    const success = await burnPlaytime(burnCount)

    if (success) {
      reloadData()
      setBurnSuccess(true)
    }
  }, [burnPlaytime, burnCount, reloadData, setBurnSuccess])

  const solarFragment = R.propOr(
    null,
    '0',
    fragments
  ) as BattleDemoFragment | null

  const lunarFragment = R.propOr(
    null,
    '1',
    fragments
  ) as BattleDemoFragment | null

  useEffect(() => {
    reloadData()
  }, [])

  return (
    <Container className={className} in={show}>
      <ConversionFlow
        src={mobile ? MobileConversionPNG.src : ConversionPNG.src}
      />
      <FragmentContainer>
        <FragmentSection consume={burnCount} fragment={lunarFragment} />
        <FragmentSection consume={burnCount} fragment={solarFragment} />
      </FragmentContainer>
      <StyledBurnSection />
      <ButtonSection>
        <BurnButton
          disabled={
            R.isNil(solarFragment) ||
            R.isNil(lunarFragment) ||
            (burnCount * solarFragment.burnRatio > solarFragment.total &&
              burnCount * lunarFragment.burnRatio > lunarFragment.total)
          }
          onClick={handleOnBurnTime}
        >
          {t('battle_demo.pass.button.start_burn')}
        </BurnButton>
        <Remark>{t('battle_demo.burn_to_get')}</Remark>
      </ButtonSection>
      <BurnSuccessPopup />
    </Container>
  )
}

type Props = {
  className?: string
  show: boolean
}

export default BattlepassContainer
